import React, { useEffect, useMemo, useState } from 'react';
import useManageCrud from 'src/@manage/use-manage.js';
import { makePaginatedTableColumns } from 'lib/helpers/table.jsx';
import Input from 'lib/components/input.jsx';
import SearchPanel from 'src/components/search-panel.jsx';
import SearchResults from 'src/components/search-results.jsx';
import { intlHeaderTexts } from '#lib/helpers/intlHeaders.js';
import { LoadingSkeleton, LoadingIndicator } from '#lib/components/loading.jsx';
import { cn } from '#lib/helpers/class.js';
import useDebounce from '#lib/hooks/use-debounce.js';
import useUserQueries from '#hooks/use-user-query.js';
import Combobox from '#lib/components/combobox';
import { useSearch } from '../hooks/use-search.js';
import { useInView } from 'react-intersection-observer';

export default function TableSearch() {
    const [query, setQuery] = useState('');
    const [selectedUserId, setSelectedUserId] = useState('');
    const [searchParams, setSearchParams] = useState({ query: '', user: '' });
    const [userQuery, setUserQuery] = useState('');
    const search = useDebounce(userQuery.toLocaleLowerCase(), 300);
    const { usersList, isSuccess } = useUserQueries(search);
    const totalUsers = useMemo(() => usersList, [isSuccess]);

    // Manage search
    const { data, fetchNextPage, isLoading, ...queryProps } = useSearch({
        query: searchParams.query,
        userId: searchParams.user,
    });

    const { ref, inView } = useInView();

    useEffect(() => {
        if (inView) fetchNextPage();
    }, [fetchNextPage, inView]);

    useEffect(() => {
        const user = usersList?.find((user) => user.id === selectedUserId);
        if (!user) return;
        setUserQuery(user?.firstName + ' ' + user?.lastName || '');
    }, [usersList, selectedUserId]);

    const handleChangeUser = (value) => {
        if (!value) {
            setUserQuery('');
            setSelectedUserId('');
            return;
        }
        setSelectedUserId(value.id);
        setUserQuery(value.firstName + ' ' + value.lastName);
    };

    const handleSearch = () => {
        setSearchParams((prev) => ({ ...prev, user: selectedUserId, query }));
    };

    useEffect(() => {
        if (!query) handleSearch();
    }, [query]);

    const columnProps = makePaginatedTableColumns(
        data,
        {
            searchParams
        },
        {
            omit: ['id', 'cover', 'tags'],
            sortable: false,
            isLoading: isLoading,
            headerText: (accessor) => intlHeaderTexts(_t.crud.itinerary.headers)(accessor),
            users: usersList,
        }
    );

    return (
        <main className="p-4 pt-[130px]">
            <SearchPanel onSubmit={handleSearch}>
                <Input.Full
                    className="flex-grow"
                    type="search"
                    label={_t.global.freeTextSearch()}
                    value={query}
                    placeholder={_t.search.namePlaceholder()}
                    onChange={(e) => setQuery(e.target.value)}
                />
                {totalUsers?.length > 1 && (
                    <>
                        <Combobox.Full
                            inPortal={false}
                            query={userQuery}
                            onQueryChange={(value) => {
                                setUserQuery(value);
                            }}
                            onInputFocus={(e) => {
                                setUserQuery(e.target.value);
                            }}
                            onChange={handleChangeUser}
                            displayValue={(opt) => {
                                return opt.firstName + ' ' + opt.lastName;
                            }}
                            label={_t.edit.fields.assignedTo()}
                            placeholder={_t.edit.fields.assignedTo()}
                            options={usersList}
                            filterFn={() => true}
                            style={{
                                minWidth: '200px',
                                width: '30%'
                            }}
                            nullable={true}
                        ></Combobox.Full>
                        <input type="hidden" name="user" value={selectedUserId} />
                    </>
                )}
            </SearchPanel>
            {(isLoading) && (
                <LoadingSkeleton className="h-screen flex items-center justify-center fixed inset-4 ">
                    <LoadingIndicator></LoadingIndicator>
                </LoadingSkeleton>
            )}
            {!isLoading && (
                <div>
                    <SearchResults
                        data={data}
                        isLoading={isLoading}
                        {...queryProps}
                        {...columnProps}
                    />
                </div>
            )}

            <div className="w-full flex justify-center items-center" ref={ref} />
        </main>
    );
}
